import { useEffect, useRef, useState } from 'react';
import { Show } from '@elfsight/lib-utils';
import { useLingui } from '@lingui/react/macro';
import { PaymentElement, useCheckout } from '@stripe/react-stripe-js';
import { useSettings } from '@wu/widget-core';
import { noop } from 'es-toolkit';
import type { PaymentSuccessHandler } from '../common';
import {
  isPaymentSessionStatus,
  isSuccessfulSessionStatusData,
  pollSessionStatus
} from '../common';
import {
  CancellationPolicyText,
  CheckoutFormWrapper,
  PayButton,
  PaymentErrorMessage,
  PaymentFormTitleBlock,
  Section,
  SecurePaymentCaption,
  StripeFormLoadingContainer,
  TotalAmountBlock
} from './components';
import { useSessionMetadata } from './session-metadata-context';

type StripeCheckoutFormProps = {
  onPaymentSuccess: PaymentSuccessHandler;
  color: string;
  accentColor: string;
  onReady?: () => void;
};

export function StripeCheckoutForm({
  onPaymentSuccess,
  color,
  accentColor,
  onReady = noop
}: StripeCheckoutFormProps) {
  const { t } = useLingui();
  const { confirm, total } = useCheckout();
  const { widgetToken } = useSettings();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();

  const {
    sessionId,
    setSessionStatus,
    isPaymentInProcess,
    setIsPaymentInProcess
  } = useSessionMetadata();

  const cancelPollRef = useRef<AbortController>();

  const handlePay = () => {
    setError(undefined);

    if (isPaymentInProcess) {
      setError(t`Payment is already in process`);

      return;
    }

    if (!sessionId) {
      setError(t`Payment session id is undefined`);
      setIsPaymentInProcess(false);

      return;
    }

    setIsPaymentInProcess(true);
    setSessionStatus('open');

    confirm({ redirect: 'if_required' })
      .then((result) => {
        if (result.type === 'error') {
          throw new Error(result.error.message);
        }

        cancelPollRef.current = new AbortController();

        return pollSessionStatus({
          sessionId,
          abortSignal: cancelPollRef.current.signal,
          widgetToken,
          validateStatus: ({ status }) => {
            const isKnownStatus = isPaymentSessionStatus(status);

            if (isKnownStatus) {
              setSessionStatus(status);
            }

            // when returns true - polling will stop
            return status !== 'open';
          }
        });
      })
      .then(async (statusPayload) => {
        if (isSuccessfulSessionStatusData(statusPayload)) {
          await onPaymentSuccess(statusPayload);

          return;
        }

        const { status, failureCode, failureMessage, paymentStatus } =
          statusPayload;

        if (!isPaymentSessionStatus(status)) {
          throw new Error(t`Unknown payment session status: ${status}`);
        }

        const failureError = failureMessage || failureCode;

        if (failureError) {
          throw new Error(failureError);
        }

        if (paymentStatus !== 'paid') {
          throw new Error(t`Payment failed`);
        }

        if (status !== 'complete') {
          throw new Error(t`Payment session was expired or canceled`);
        }

        throw new Error(t`Something went wrong`);
      })
      .catch((error) => {
        setSessionStatus('error');

        if (error instanceof Error) {
          setError(error.message);
        }
      })
      .finally(() => {
        setIsPaymentInProcess(false);
      });
  };

  useEffect(() => {
    onReady();

    return () => cancelPollRef.current?.abort();
  }, [onReady]);

  return (
    <CheckoutFormWrapper $color={color} $disabled={isPaymentInProcess}>
      <TotalAmountBlock amount={total.total.amount} color={color} />

      <PaymentFormTitleBlock />

      <StripeFormLoadingContainer isLoading={isLoading}>
        <PaymentElement
          options={{
            paymentMethodOrder: ['card'],
            layout: 'tabs',
            readOnly: isPaymentInProcess
          }}
          onReady={() => setIsLoading(false)}
        />
      </StripeFormLoadingContainer>

      <Section>
        <PayButton
          onClick={handlePay}
          disabled={isPaymentInProcess}
          color={accentColor}
        />

        <Show when={!!error}>
          <PaymentErrorMessage>{error}</PaymentErrorMessage>
        </Show>

        <SecurePaymentCaption color={color} />
      </Section>

      <CancellationPolicyText />
    </CheckoutFormWrapper>
  );
}
